import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave,
  faGear,
  faSitemap,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../services/api';
import './Settings.css';

const Settings = () => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await apiService.getSettings();
      
      // Convert array to object for form
      const data = {};
      response.data.forEach(setting => {
        data[setting.key] = setting.value;
      });
      setFormData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'true' : 'false') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      await apiService.updateSettings(formData);
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSitemap = async () => {
    setGenerating(true);
    setMessage('');

    try {
      const response = await apiService.generateSitemap();
      setMessage(response.data.message);
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      console.error('Error generating sitemap:', error);
      setMessage('Failed to generate sitemap');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <div className="settings-loading">Loading settings...</div>;
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div>
          <h2>
            <FontAwesomeIcon icon={faGear} style={{ marginRight: '12px', color: '#8b5cf6' }} />
            Blog Settings
          </h2>
          <p>Configure your blog preferences and SEO settings</p>
        </div>
      </div>

      {message && (
        <div className={`settings-message ${message.includes('Failed') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="settings-section">
          <h3>General Settings</h3>
          <div className="settings-grid">
            <div className="settings-field">
              <label htmlFor="site_name">Site Name</label>
              <input
                type="text"
                id="site_name"
                name="site_name"
                value={formData.site_name || ''}
                onChange={handleChange}
              />
              <small>Your blog name displayed across the site</small>
            </div>

            <div className="settings-field">
              <label htmlFor="site_url">Site URL</label>
              <input
                type="url"
                id="site_url"
                name="site_url"
                value={formData.site_url || ''}
                onChange={handleChange}
              />
              <small>Full URL of your blog</small>
            </div>

            <div className="settings-field settings-field-full">
              <label htmlFor="site_description">Site Description</label>
              <textarea
                id="site_description"
                name="site_description"
                value={formData.site_description || ''}
                onChange={handleChange}
                rows="3"
              />
              <small>Short description for SEO and meta tags</small>
            </div>

            <div className="settings-field">
              <label htmlFor="posts_per_page">Posts Per Page</label>
              <input
                type="number"
                id="posts_per_page"
                name="posts_per_page"
                value={formData.posts_per_page || '10'}
                onChange={handleChange}
                min="1"
                max="50"
              />
              <small>Number of posts to display per page</small>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>Sitemap Settings</h3>
          <div className="settings-grid">
            <div className="settings-field">
              <label className="settings-checkbox-label">
                <input
                  type="checkbox"
                  name="sitemap_enabled"
                  checked={formData.sitemap_enabled === 'true'}
                  onChange={handleChange}
                />
                <span>Enable Automatic Sitemap Generation</span>
              </label>
              <small>Automatically update sitemap.xml on schedule</small>
            </div>

            <div className="settings-field">
              <label htmlFor="sitemap_schedule">Generation Schedule</label>
              <select
                id="sitemap_schedule"
                name="sitemap_schedule"
                value={formData.sitemap_schedule || 'daily'}
                onChange={handleChange}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
              <small>How often to regenerate the sitemap</small>
            </div>

            <div className="settings-field">
              <label htmlFor="sitemap_time">Generation Time</label>
              <input
                type="time"
                id="sitemap_time"
                name="sitemap_time"
                value={formData.sitemap_time || '00:00'}
                onChange={handleChange}
              />
              <small>Time of day to generate sitemap (24-hour format)</small>
            </div>

            <div className="settings-field">
              <button
                type="button"
                onClick={handleGenerateSitemap}
                className="settings-btn settings-btn-secondary"
                disabled={generating}
              >
                <FontAwesomeIcon icon={generating ? faSpinner : faSitemap} spin={generating} />
                {generating ? 'Generating...' : 'Generate Sitemap Now'}
              </button>
              <small>Manually generate sitemap.xml</small>
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button type="submit" className="settings-btn settings-btn-primary" disabled={saving}>
            <FontAwesomeIcon icon={faSave} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
